<template>
  <div class="lg:grid lg:grid-cols-[15.625rem_1fr]">
    <NavigationSideBar
      :menu-items="filteredMenuItems"
      :user="currentUser"
      :settings="filteredMenuItems.filter((e) => e.name === 'nav.settings').length > 0"
      :show-settings="showSettings"
      @logout="logout()"
    />

    <router-view />

    <NavigationBottomBar :menu-items="filteredMenuItems" :show-settings="showSettings" @logout="logout()" />

    <InfuseNotificationGroup v-slot="{ notifications, close }" group="notify-top">
      <InfuseNotification
        v-for="(notification, i) in notifications"
        :key="`t-infuse-notification-${i}`"
        :notification="notification"
        @click="close(i)"
      />
    </InfuseNotificationGroup>

    <InfuseNotificationGroup v-slot="{ notifications, close }" group="notify-bottom" position="bottom">
      <InfuseNotification
        v-for="(notification, i) in notifications"
        :key="`b-infuse-notification-${i}`"
        :notification="notification"
        @click="close(i)"
      />
    </InfuseNotificationGroup>
  </div>
</template>

<script lang="ts" setup>
import { useAuthenticator, User, useUserService } from '@infuse/auth/services';

import { InfuseNotification, InfuseNotificationGroup } from '@infuse/ui/components';
import { MenuItem } from '@infuse/ui/domain';
import { UiCurrentMenuInjectionToken } from '@infuse/ui/services';
import { computed, onMounted, provide, ref, unref } from 'vue';
import { RouteLocationMatched, useRoute, useRouter } from 'vue-router';

import NavigationSideBar from './NavigationSideBar.vue';
import NavigationBottomBar from './NavigationBottomBar.vue';
import { useCurrentUserStore } from '@infuse/iam/user/store';

const routeToMenuItem = (route: RouteLocationMatched): MenuItem => ({
  name: route.meta.name as string,
  icon: route.meta.icon as string,
  group: route.meta.group as string,
  permissionIds: route.meta.permissionIds as string[],
  permissionCheck: route.meta.permissionCheck as 'any' | 'all',
  to: route.path
});

const currentUserStore = useCurrentUserStore();
const authenticator = useAuthenticator();
const route = useRoute();
const router = useRouter();
const userService = useUserService();

const currentUser = ref<User>();

const menuItems = computed(() => {
  const routes = router.getRoutes();

  return routes.filter((r) => r.meta && r.meta.name).map<MenuItem>(routeToMenuItem);
});

const filteredMenuItems = computed(() => {
  const routes = router.getRoutes();

  return routes.filter((r) => r.meta && r.meta.name && !r.meta.hidden).map<MenuItem>(routeToMenuItem);
});

const currentItem = computed(() => {
  const currentRoute = route.matched[route.matched.length - 1];
  const items = unref(menuItems);

  return items.find((i) => currentRoute.path.includes(i.to));
});

provide(UiCurrentMenuInjectionToken, currentItem);

async function logout() {
  await authenticator.logout();
  currentUserStore.reset();
  await router.push({ name: 'auth-login' });
}

onMounted(async () => {
  currentUser.value = await userService.getUser();
});

const showSettings = computed(() => {
  return router.getRoutes().find((route) => route.name === 'main')?.meta?.hideSettings !== true;
});
</script>
